import React from "react";

import {
  AgentIcon,
  CalendarIcon,
  ClockIcon,
  CloudUploadIcon,
  DocumentIcon,
  FollowUpIcon,
  GlobeIcon,
  LocationIcon,
  PathIcon,
  PhoneIcon,
  PlusIcon,
  SearchIcon,
  SortIcon,
  TagIcon,
  UserGroupIcon,
} from "../../lib/icons";
import { LEAD_STATUS_SEQUENCE } from "../../lib/constants";
import { buildOrderedLeadSourceOptions } from "../../lib/utils";
import DateRangeModal from "./DateRangeModal";
import MultiSelect from "./MultiSelect";

export const VALID_CITIES = [
  'Delhi', 'Bangalore', 'Hyderabad', 'Ghaziabad', 'Gurugram',
  'Noida', 'Chandigarh', 'Lucknow', 'Chennai', 'Mumbai',
  'Jaipur', 'Pune', 'Kolkata', 'Others'
];

export const CITY_REGION_MAP = {
  'Delhi': 'North',
  'Bangalore': 'South',
  'Hyderabad': 'South',
  'Ghaziabad': 'North',
  'Gurugram': 'North',
  'Noida': 'North',
  'Chandigarh': 'North',
  'Lucknow': 'North',
  'Chennai': 'South',
  'Mumbai': 'West',
  'Jaipur': 'North',
  'Pune': 'West',
  'Kolkata': 'East',
  'Others': ''
};

function LeadFilters({
  agents,
  availableCities,
  availableRegions,
  availableSubDispositions,
  canBulkAssign,
  canBulkUpload,
  canCreateLead,
  filters,
  leadSources,
  onAddLead,
  onBulkAssign,
  onBulkUpload,
  onClearFilters,
  onDownloadLeads,
  onFullDataExport,
  onFilterChange,
  onSearchChange,
  roleScope,
  rootDispositions,
  search,
  canExportLeads,
}) {
  const hasAdvancedFilters = Boolean(
    filters.sourceId ||
      filters.assignedToId ||
      filters.region ||
      filters.city ||
      filters.dispositionRootId ||
      filters.subDispositionId ||
      filters.status ||
      filters.customerStatus ||
      filters.followUpBucket ||
      filters.timeLeft ||
      filters.attemptedCalls ||
      filters.connectedCalls ||
      filters.registeredDate ||
      filters.registeredDateEnd ||
      filters.campaignType ||
      (filters.ordering && filters.ordering !== "-registered_at"),
  );
  const [filtersOpen, setFiltersOpen] = React.useState(hasAdvancedFilters);
  const [dateModalOpen, setDateModalOpen] = React.useState(false);
  const [quickCategory, setQuickCategory] = React.useState(null);
  const [cityGroup, setCityGroup] = React.useState(null);
  const [advCityOpen, setAdvCityOpen] = React.useState(false);
  const [advCityGroup, setAdvCityGroup] = React.useState(null);
  const [cityModalOpen, setCityModalOpen] = React.useState(false);
  const [cityModalTab, setCityModalTab] = React.useState('targeted');
  const [cityModalSearch, setCityModalSearch] = React.useState('');
  const [addMenuOpen, setAddMenuOpen] = React.useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = React.useState(false);
  const addMenuRef = React.useRef(null);
  const downloadMenuRef = React.useRef(null);

  React.useEffect(() => {
    if (!addMenuOpen) return undefined;
    function handleClickOutside(event) {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setAddMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [addMenuOpen]);

  React.useEffect(() => {
    if (!downloadMenuOpen) return undefined;
    function handleClickOutside(event) {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setDownloadMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [downloadMenuOpen]);

  React.useEffect(() => {
    const cities = filters.city ? String(filters.city).split(",").map((s) => s.trim()).filter(Boolean) : [];
    if (cities.includes("Others")) setFiltersOpen(true);
  }, [filters.city]);

  React.useEffect(() => {
    if (quickCategory !== 'city') setCityGroup(null);
  }, [quickCategory]);
  function splitCsv(v) {
    return v ? String(v).split(",").map((s) => s.trim()).filter(Boolean) : [];
  }

  function toggleCsv(key, val) {
    const current = splitCsv(filters[key]);
    const k = String(val);
    const next = current.includes(k) ? current.filter((v) => v !== k) : [...current, k];
    
    if (key === "city") {
      onFilterChange("city", next.join(","));
      if (next.includes("Others")) {
        onFilterChange("region", "");
      } else if (next.length > 0) {
        const mappedRegions = Array.from(new Set(next.map(c => CITY_REGION_MAP[c]).filter(Boolean)));
        onFilterChange("region", mappedRegions.join(","));
      } else {
        onFilterChange("region", "");
      }
    } else {
      onFilterChange(key, next.join(","));
    }
  }

  function removeQuickSelection(event, key, value) {
    event.stopPropagation();
    const next = splitCsv(filters[key]).filter((item) => item !== String(value));
    onFilterChange(key, next.join(","));
  }

  function formatShortDate(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y.slice(2)}`;
  }
  const dateLabel = filters.registeredDate || filters.registeredDateEnd
    ? `Filter by Date: ${formatShortDate(filters.registeredDate) || "…"} – ${formatShortDate(filters.registeredDateEnd) || "…"}`
    : "Filter by Date";

  const heading = "Leads";

  const sourceOptions = buildOrderedLeadSourceOptions(leadSources).filter(
    (option) => option.label !== "Other Sources",
  );

  const REGION_ORDER = ["North", "South", "West", "East"];
  const regionOptions = Array.from(
    new Map(
      (availableRegions || [])
        .map((r) => String(r).trim())
        .filter((r) => {
          const lower = r.toLowerCase();
          return lower !== "other cities" && lower !== "other city" && lower !== "others" && r !== "";
        })
        .map((r) => [r.toLowerCase(), r])
    ).values()
  )
    .sort((a, b) => {
      const ai = REGION_ORDER.indexOf(a);
      const bi = REGION_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    })
    .map((region) => ({ value: region, label: region }));

  const TARGETED_CITIES = ['Delhi', 'Noida', 'Gurugram', 'Ghaziabad', 'Chandigarh', 'Mumbai', 'Hyderabad', 'Bangalore', 'Chennai'];

  // Merge known cities with any unknown-city nodes from backend (new FB cities get their own node)
  const unknownBackendCities = (availableCities || [])
    .filter((c) => c && !VALID_CITIES.includes(c) && c !== 'Others')
    .sort();
  const allCityList = [
    ...VALID_CITIES.filter((c) => c !== 'Others'),
    ...unknownBackendCities,
    'Others',
  ];
  const cityOptions = allCityList.map((c) => ({ value: c, label: c }));
  const nonTargetedCities = allCityList.filter((c) => !TARGETED_CITIES.includes(c));

  const agentOptions = [
    { value: 'unassigned', label: 'Unassigned' },
    ...agents.map((a) => ({ value: String(a.id), label: a.full_name })),
  ];

  const statusOptions = LEAD_STATUS_SEQUENCE;

  const dispositionOptions = rootDispositions.map((d) => ({ value: String(d.id), label: d.name }));
  const subDispositionOptions = availableSubDispositions.map((d) => ({
    value: String(d.id), label: d.name,
  }));
  const MAX_PREVIEW_ITEMS = 2;

  return (
    <section className="toolbar-panel">
      <div className="toolbar-header">
        <h1>{heading}</h1>
        <div className="toolbar-actions leads-toolbar-actions">
          {roleScope?.can_see_franchise ? (
            <button
              type="button"
              className="filter-toolbar-button compact-button"
              title={filters.showFranchise ? "Showing franchise leads — click to hide" : "Show franchise leads"}
              onClick={() => onFilterChange("showFranchise", !filters.showFranchise)}
              style={filters.showFranchise
                ? { background: "#dcfce7", color: "#15803d", border: "1px solid #86efac" }
                : { background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5" }
              }
            >
              <TagIcon />
              <span>{filters.showFranchise ? "Franchise: ON" : "Franchise: OFF"}</span>
            </button>
          ) : null}

          {canBulkAssign ? (
            <button type="button" className="filter-toolbar-button compact-button" onClick={onBulkAssign}>
              <UserGroupIcon />
              <span>Bulk Reassign</span>
            </button>
          ) : null}

          {(canBulkUpload || canCreateLead) ? (
            <div className="add-lead-menu" ref={addMenuRef}>
              <button
                type="button"
                className="filter-toolbar-button compact-button add-lead-trigger"
                onClick={() => setAddMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={addMenuOpen}
              >
                <PlusIcon />
                <span>Add / Import Lead</span>
                <span className="add-lead-caret" aria-hidden="true">▾</span>
              </button>
              {addMenuOpen ? (
                <div className="add-lead-dropdown" role="menu">
                  {canBulkUpload ? (
                    <button
                      type="button"
                      className="add-lead-option"
                      role="menuitem"
                      onClick={() => { setAddMenuOpen(false); onBulkUpload(); }}
                    >
                      <CloudUploadIcon />
                      <span>Bulk Upload</span>
                    </button>
                  ) : null}
                  {canCreateLead ? (
                    <button
                      type="button"
                      className="add-lead-option"
                      role="menuitem"
                      onClick={() => { setAddMenuOpen(false); onAddLead(); }}
                    >
                      <PlusIcon />
                      <span>Add New Lead</span>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="leads-toolbar-lower">
        <div className="toolbar-actions leads-toolbar-search-row">
          <label className="search-box leads-search-grow">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search leads..."
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
            />
          </label>

          <button
            type="button"
            className="filter-toolbar-button compact-button date-filter-button"
            onClick={() => setDateModalOpen(true)}
          >
            <CalendarIcon />
            <span>{dateLabel}</span>
          </button>

          <button
            type="button"
            className={`filter-toolbar-button compact-button leads-filters-button${filtersOpen ? " active" : ""}`}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            {filtersOpen ? "Hide Filters" : "Filters"}
          </button>

          {canExportLeads ? (
            <div className="add-lead-menu" ref={downloadMenuRef}>
              <button
                type="button"
                className="filter-toolbar-button compact-button add-lead-trigger"
                onClick={() => setDownloadMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={downloadMenuOpen}
              >
                <DocumentIcon />
                <span>Download Leads</span>
                <span className="add-lead-caret" aria-hidden="true">▾</span>
              </button>
              {downloadMenuOpen ? (
                <div className="add-lead-dropdown" role="menu">
                  <button
                    type="button"
                    className="add-lead-option"
                    role="menuitem"
                    onClick={() => { setDownloadMenuOpen(false); onDownloadLeads("xlsx"); }}
                  >
                    <DocumentIcon />
                    <span>XLSX</span>
                  </button>
                  <button
                    type="button"
                    className="add-lead-option"
                    role="menuitem"
                    onClick={() => { setDownloadMenuOpen(false); onDownloadLeads("csv"); }}
                  >
                    <DocumentIcon />
                    <span>CSV</span>
                  </button>
                  {onFullDataExport ? (
                    <button
                      type="button"
                      className="add-lead-option"
                      role="menuitem"
                      onClick={() => { setDownloadMenuOpen(false); onFullDataExport(); }}
                    >
                      <DocumentIcon />
                      <span>Full Data Export</span>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="quick-filters">
        <span className="quick-filters-label" aria-hidden="true">
          Quick <span className="quick-filters-bolt">⚡</span>
        </span>
        {[
          { key: "city", label: "City", icon: <LocationIcon />, filterKey: "city", options: cityOptions },
          { key: "agents", label: "Agents", icon: <AgentIcon />, filterKey: "assignedToId", options: agentOptions },
          { key: "region", label: "Region", icon: <GlobeIcon />, filterKey: "region", options: regionOptions },
          { key: "status", label: "Customer Status", icon: <TagIcon />, filterKey: "status", options: statusOptions },
          { key: "disposition", label: "Dispositions", icon: <PathIcon />, filterKey: "dispositionRootId", options: dispositionOptions },
        ].map((cat) => {
          const active = quickCategory === cat.key;
          const selected = splitCsv(filters[cat.filterKey]);
          const selectedLabels = cat.options
            .filter((opt) => selected.includes(String(opt.value)))
            .map((opt) => ({ value: String(opt.value), label: opt.label }));
          const visibleSelectedLabels = selectedLabels.slice(0, MAX_PREVIEW_ITEMS);
          const hiddenSelectedCount = Math.max(0, selectedLabels.length - visibleSelectedLabels.length);
          return (
            <div key={cat.key} className="quick-filters-group">
              <button
                type="button"
                className={`quick-filter-chip${active ? " active" : ""}${selected.length ? " has-selection" : ""}`}
                title={selectedLabels.map((option) => option.label).join(", ")}
                onClick={() => setQuickCategory(active ? null : cat.key)}
              >
                <span className="quick-filter-chip-icon">{cat.icon}</span>
                <span>{cat.label}</span>
                {selectedLabels.length ? (
                  <span className="quick-filter-selection-preview">
                    {visibleSelectedLabels.map((option) => (
                      <span key={option.value} className="quick-filter-selection-pill">
                        <span>{option.label}</span>
                        <span
                          role="button"
                          aria-label={`Remove ${option.label}`}
                          className="quick-filter-selection-remove"
                          onClick={(event) => removeQuickSelection(event, cat.filterKey, option.value)}
                        >
                          ×
                        </span>
                      </span>
                    ))}
                    {hiddenSelectedCount ? (
                      <span className="quick-filter-selection-pill summary">
                        +{hiddenSelectedCount} more
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </button>
              {active && (
                <div className="quick-sub-filters">
                  {cat.options.length === 0 ? (
                    <span className="quick-empty">No options</span>
                  ) : cat.key === "city" ? (
                    <button
                      type="button"
                      className="quick-sub-chip city-group-chip"
                      onClick={() => { setCityModalOpen(true); setQuickCategory(null); }}
                    >
                      <span>Browse Cities…</span>
                      <span>›</span>
                    </button>
                  ) : (
                    cat.options.map((opt) => {
                      const on = selected.includes(String(opt.value));
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`quick-sub-chip${on ? " on" : ""}`}
                          onClick={() => toggleCsv(cat.filterKey, opt.value)}
                        >
                          {opt.label}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <DateRangeModal
        open={dateModalOpen}
        startValue={filters.registeredDate}
        endValue={filters.registeredDateEnd}
        onClose={() => setDateModalOpen(false)}
        onApply={(start, end) => {
          onFilterChange("registeredDate", start);
          onFilterChange("registeredDateEnd", end);
          setDateModalOpen(false);
        }}
      />

      {filtersOpen && (
        <div className="filter-panel filter-panel-inline filter-panel-leads">
          <label className="field-stack">
            <span className="filter-label-with-icon"><GlobeIcon />Source</span>
            <MultiSelect
              options={sourceOptions}
              value={filters.sourceId}
              onChange={(val) => onFilterChange("sourceId", val)}
              placeholder="All sources"
            />
          </label>

          {roleScope?.scope !== "own_leads" ? (
            <label className="field-stack">
              <span className="filter-label-with-icon"><AgentIcon />Agent</span>
              <MultiSelect
                options={agentOptions}
                value={filters.assignedToId}
                onChange={(val) => onFilterChange("assignedToId", val)}
                placeholder="All agents"
              />
            </label>
          ) : null}

          <label className="field-stack">
            <span className="filter-label-with-icon"><GlobeIcon />Region</span>
            <MultiSelect
              options={regionOptions}
              value={filters.region}
              onChange={(val) => onFilterChange("region", val)}
              placeholder="All regions"
            />
          </label>

          <label className="field-stack">
            <span className="filter-label-with-icon"><LocationIcon />City</span>
            <button type="button" className="adv-city-group-btn" onClick={() => setCityModalOpen(true)}>
              <span>{splitCsv(filters.city).length > 0 ? `${splitCsv(filters.city).length} selected` : 'Select city'}</span>
              <span>›</span>
            </button>
            {splitCsv(filters.city).includes("Others") && (
              <label style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "5px", fontSize: "0.78em", color: "var(--text-secondary, #666)", cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  checked={Boolean(filters.showOthersDetail)}
                  onChange={(e) => onFilterChange("showOthersDetail", e.target.checked)}
                  style={{ accentColor: "var(--color-primary, #4f46e5)", cursor: "pointer" }}
                />
                Show raw FB city
              </label>
            )}
          </label>

          <label className="field-stack">
            <span className="filter-label-with-icon"><TagIcon />Status</span>
            <MultiSelect
              options={statusOptions}
              value={filters.status}
              onChange={(val) => onFilterChange("status", val)}
              placeholder="All"
            />
          </label>

          <label className="field-stack">
            <span className="filter-label-with-icon"><FollowUpIcon />Follow-up</span>
            <select
              value={filters.followUpBucket}
              onChange={(event) => onFilterChange("followUpBucket", event.target.value)}
            >
              <option value="">All leads</option>
              <option value="pending">Pending</option>
              <option value="today">Today</option>
            </select>
          </label>

          <label className="field-stack">
            <span className="filter-label-with-icon"><ClockIcon />Time Left</span>
            <select
              value={filters.timeLeft}
              onChange={(event) => onFilterChange("timeLeft", event.target.value)}
            >
              <option value="">All</option>
              <option value="overdue">Overdue</option>
              <option value="within_2h">&lt; 2 hrs</option>
              <option value="within_6h">&lt; 6 hrs</option>
              <option value="within_12h">&lt; 12 hrs</option>
              <option value="within_24h">&lt; 24 hrs</option>
              <option value="na">NA (Already called)</option>
            </select>
          </label>

          <label className="field-stack">
            <span className="filter-label-with-icon"><PhoneIcon />Attempted Calls</span>
            <select
              value={filters.attemptedCalls}
              onChange={(event) => onFilterChange("attemptedCalls", event.target.value)}
            >
              <option value="">All</option>
              <option value="0">0</option>
              <option value="1-3">1-3</option>
              <option value="4-6">4-6</option>
              <option value="more_than_6">More than 6</option>
            </select>
          </label>

          <label className="field-stack">
            <span className="filter-label-with-icon"><PhoneIcon />Connected Calls</span>
            <select
              value={filters.connectedCalls}
              onChange={(event) => onFilterChange("connectedCalls", event.target.value)}
            >
              <option value="">All</option>
              <option value="0">0</option>
              <option value="1-2">1-2</option>
              <option value="3-5">3-5</option>
              <option value="more_than_5">More than 5</option>
            </select>
          </label>

          <label className="field-stack">
            <span className="filter-label-with-icon"><PathIcon />Disposition</span>
            <MultiSelect
              options={dispositionOptions}
              value={filters.dispositionRootId}
              onChange={(val) => onFilterChange("dispositionRootId", val)}
              placeholder="All dispositions"
            />
          </label>

          {filters.dispositionRootId && subDispositionOptions.length > 0 ? (
            <label className="field-stack">
              <span className="filter-label-with-icon"><PathIcon />Sub-Disposition</span>
              <MultiSelect
                options={subDispositionOptions}
                value={filters.subDispositionId}
                onChange={(val) => onFilterChange("subDispositionId", val)}
                placeholder="All sub-dispositions"
              />
            </label>
          ) : null}

          {roleScope?.can_see_franchise && filters.showFranchise ? (
            <label className="field-stack">
              <span className="filter-label-with-icon"><TagIcon />Campaign Type</span>
              <select
                value={filters.campaignType || ""}
                onChange={(event) => onFilterChange("campaignType", event.target.value)}
              >
                <option value="">All</option>
                <option value="b2c">B2C</option>
                <option value="franchise">Franchise</option>
              </select>
            </label>
          ) : null}

          <label className="field-stack">
            <span className="filter-label-with-icon"><SortIcon />Sort</span>
            <select
              value={filters.ordering}
              onChange={(event) => onFilterChange("ordering", event.target.value)}
            >
              <option value="-registered_at">Newest first</option>
              <option value="registered_at">Oldest first</option>
              <option value="-next_follow_up_at">Latest follow-up</option>
              <option value="next_follow_up_at">Earliest follow-up</option>
            </select>
          </label>

          {roleScope?.can_see_franchise && filters.showFranchise ? (
            <label className="field-stack">
              <span className="filter-label-with-icon"><TagIcon />Campaign Type</span>
              <select
                value={filters.campaignType || ""}
                onChange={(event) => onFilterChange("campaignType", event.target.value)}
              >
                <option value="">All</option>
                <option value="b2c">B2C</option>
                <option value="franchise">Franchise</option>
              </select>
            </label>
          ) : null}

          <div className="filter-actions-inline">
            <button type="button" className="filter-toolbar-button compact-button filter-clear-button" onClick={onClearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* ── City picker modal ──────────────────────────────────── */}
      {cityModalOpen && (() => {
        const selectedCities = splitCsv(filters.city);
        const tabCities = cityModalTab === 'targeted' ? TARGETED_CITIES : nonTargetedCities;
        const filtered = cityModalSearch
          ? tabCities.filter((c) => c.toLowerCase().includes(cityModalSearch.toLowerCase()))
          : tabCities;

        function toggleCity(city) {
          const next = selectedCities.includes(city)
            ? selectedCities.filter((c) => c !== city)
            : [...selectedCities, city];
          onFilterChange('city', next.join(','));
          if (next.includes('Others')) {
            onFilterChange('region', '');
          } else if (next.length > 0) {
            const regions = Array.from(new Set(next.map((c) => CITY_REGION_MAP[c]).filter(Boolean)));
            onFilterChange('region', regions.join(','));
          } else {
            onFilterChange('region', '');
          }
          if (!next.includes('Others')) onFilterChange('showOthersDetail', false);
        }

        return (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.35)', backdropFilter: 'blur(2px)' }}
            onMouseDown={() => { setCityModalOpen(false); setCityModalSearch(''); }}
          >
            <div
              style={{ background: '#fff', borderRadius: 14, width: 440, maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Select Cities</span>
                <button
                  onClick={() => { setCityModalOpen(false); setCityModalSearch(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1, padding: '0 2px' }}
                >×</button>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
                {[
                  { key: 'targeted', label: 'Targeted', count: TARGETED_CITIES.length },
                  { key: 'non-targeted', label: 'Non Targeted', count: nonTargetedCities.length },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => { setCityModalTab(tab.key); setCityModalSearch(''); }}
                    style={{
                      flex: 1, padding: '10px 12px', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: cityModalTab === tab.key ? 700 : 500,
                      color: cityModalTab === tab.key ? '#2563eb' : '#6b7280',
                      borderBottom: cityModalTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
                      border: 'none', transition: 'all 0.15s',
                    }}
                  >
                    {tab.label}
                    <span style={{ marginLeft: 6, fontSize: 11, background: cityModalTab === tab.key ? '#dbeafe' : '#f3f4f6', color: cityModalTab === tab.key ? '#1d4ed8' : '#6b7280', borderRadius: 20, padding: '1px 7px', fontWeight: 600 }}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Search */}
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search cities…"
                  value={cityModalSearch}
                  onChange={(e) => setCityModalSearch(e.target.value)}
                  style={{ width: '100%', padding: '7px 11px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827' }}
                />
              </div>

              {/* City list */}
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {filtered.length === 0 ? (
                  <div style={{ padding: 28, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No cities found</div>
                ) : (
                  filtered.map((city) => {
                    const on = selectedCities.includes(city);
                    return (
                      <button
                        key={city}
                        type="button"
                        onClick={() => toggleCity(city)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 16px',
                          background: on ? '#eff6ff' : 'transparent', border: 'none', cursor: 'pointer',
                          textAlign: 'left', fontSize: 13, color: on ? '#1d4ed8' : '#374151',
                          borderBottom: '1px solid #f9fafb', transition: 'background 0.1s',
                        }}
                      >
                        <span style={{
                          width: 17, height: 17, borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: `1.5px solid ${on ? '#2563eb' : '#d1d5db'}`, background: on ? '#2563eb' : '#fff', transition: 'all 0.15s',
                        }}>
                          {on && <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>}
                        </span>
                        {city}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '10px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 46 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  {selectedCities.length > 0 ? `${selectedCities.length} city${selectedCities.length > 1 ? 'ies' : ''} selected` : 'None selected'}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  {selectedCities.length > 0 && (
                    <button
                      onClick={() => { onFilterChange('city', ''); onFilterChange('region', ''); }}
                      style={{ fontSize: 12, color: '#ef4444', background: 'none', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', padding: '4px 10px', fontWeight: 600 }}
                    >Clear all</button>
                  )}
                  <button
                    onClick={() => { setCityModalOpen(false); setCityModalSearch(''); }}
                    style={{ fontSize: 12, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', padding: '4px 14px', fontWeight: 600 }}
                  >Done</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </section>
  );
}

export default LeadFilters;
